import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Lock, Mail, CheckCircle2 } from "lucide-react";
import logo from "@/assets/wealthy-steward-logo.png";
import { properName } from "@/lib/names";

type LookupState =
  | { kind: "loading" }
  | { kind: "valid"; email: string; firstName: string; lastName: string }
  | { kind: "invalid"; reason: "invalid" | "used" | "expired" };

const reasonCopy: Record<string, string> = {
  invalid: "This welcome link does not look right.",
  used: "This welcome link has already been used.",
  expired: "This welcome link has expired.",
};

export default function Welcome() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = searchParams.get("t") ?? "";

  const [state, setState] = useState<LookupState>({ kind: "loading" });
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Request a new link
  const [requestEmail, setRequestEmail] = useState("");
  const [requestNote, setRequestNote] = useState("");
  const [requestSent, setRequestSent] = useState(false);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    let active = true;
    const run = async () => {
      if (!token) {
        setState({ kind: "invalid", reason: "invalid" });
        return;
      }
      try {
        const { data, error } = await supabase.functions.invoke("register-member", {
          body: { action: "lookup", token },
        });
        if (!active) return;
        if (error || !data?.valid) {
          setState({ kind: "invalid", reason: (data?.reason as never) ?? "invalid" });
          return;
        }
        setFirstName(data.first_name ?? "");
        setLastName(data.last_name ?? "");
        setState({
          kind: "valid",
          email: data.email,
          firstName: data.first_name ?? "",
          lastName: data.last_name ?? "",
        });
      } catch (_e) {
        if (active) setState({ kind: "invalid", reason: "invalid" });
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [token]);

  const handleSubmit = async () => {
    if (state.kind !== "valid") return;
    if (!firstName.trim() || !lastName.trim()) {
      toast({ variant: "destructive", title: "Almost there", description: "Please confirm your first and last name." });
      return;
    }
    if (password.length < 8) {
      toast({ variant: "destructive", title: "Almost there", description: "Please use a password with at least 8 characters." });
      return;
    }
    if (password !== confirmPassword) {
      toast({ variant: "destructive", title: "Almost there", description: "The two passwords do not match." });
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("register-member", {
        body: { token, password, first_name: properName(firstName), last_name: properName(lastName) },
      });
      if (error || !data?.success) {
        let message = data?.error as string | undefined;
        if (!message && error) {
          try {
            const parsed = await (error as { context?: Response }).context?.json();
            message = parsed?.error;
          } catch (_e) {
            message = undefined;
          }
        }
        throw new Error(message || "We could not set up your account. Please try again.");
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: state.email,
        password,
      });
      if (signInError) throw signInError;

      navigate("/welcome/success", { replace: true });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Something went wrong",
        description: (e as Error).message,
      });
    }
    setSubmitting(false);
  };

  const handleRequestLink = async () => {
    if (!requestEmail.includes("@")) {
      toast({ variant: "destructive", title: "Almost there", description: "Please enter the email you were invited with." });
      return;
    }
    setRequesting(true);
    try {
      await supabase.functions.invoke("register-member", {
        body: { action: "request_link", email: requestEmail.trim(), token, note: requestNote.trim() },
      });
      setRequestSent(true);
    } catch (_e) {
      toast({ variant: "destructive", title: "Something went wrong", description: "Please try again in a moment." });
    }
    setRequesting(false);
  };

  if (state.kind === "loading") {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (state.kind === "invalid") {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Let us get you a fresh link</CardTitle>
            <CardDescription>
              {reasonCopy[state.reason]} Welcome links last 30 days. Tell us where to send a new one and we
              will take care of it.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {requestSent ? (
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 text-sm">
                <CheckCircle2 className="mb-2 h-5 w-5 text-primary" />
                Thank you. Our team has your request and will send a new welcome link soon.
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="request-email">Your email</Label>
                  <Input
                    id="request-email"
                    type="email"
                    value={requestEmail}
                    onChange={(e) => setRequestEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="request-note">Anything we should know (optional)</Label>
                  <Textarea
                    id="request-note"
                    value={requestNote}
                    onChange={(e) => setRequestNote(e.target.value)}
                    rows={3}
                  />
                </div>
                <Button className="w-full min-h-[44px]" onClick={handleRequestLink} disabled={requesting}>
                  {requesting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Request a new link
                </Button>
              </>
            )}
            <Button variant="ghost" className="w-full min-h-[44px]" onClick={() => navigate("/login")}>
              I already have an account
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src={logo} alt="Wealthy Steward" className="h-20 w-auto" />
          </div>
          <CardTitle className="text-2xl">Welcome to Wealthy Steward!</CardTitle>
          <CardDescription>
            Your first step into stewardship is setting a password to access your portal. Once inside, you'll answer a few questions and move through a few steps. Everything happens right here in the portal. Make sure the password you choose is unique and record it for safekeeping.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Your email</Label>
            <div className="flex items-center gap-3 rounded-md border bg-muted px-3 py-3">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="flex-1 font-medium break-all">{state.email}</span>
              <Lock className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground">
              This is the email we have on file. Reach out to us if it needs to change.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="first-name">First name</Label>
              <Input
                id="first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                onBlur={() => setFirstName(properName(firstName))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last-name">Last name</Label>
              <Input
                id="last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                onBlur={() => setLastName(properName(lastName))}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Create a password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Type it again</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          <Button className="w-full min-h-[48px]" size="lg" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {submitting ? "Setting up your account" : "Create my account"}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            By creating your account, you agree to the{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline">
              Privacy Policy
            </a>
            .
          </p>

        </CardContent>
      </Card>
    </div>
  );
}
